/**
 * API functions for managing reporting periods
 * This module provides functions to interact with the reporting periods API endpoints
 */

import axios from "axios";
import { ReportingPeriod } from "../types/ReportingPeriod";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientAndFundHeader, buildClientHeader } from "./buildHeader";

/**
 * Interface for creating a new reporting period
 */
export interface CreateReportingPeriodRequest {
  /** The period identifier (e.g. "Q1 2023") */
  period: string;
  /** Whether the reporting period is currently open */
  isOpen?: boolean;
}

/**
 * Fetches all reporting periods for a specific fund
 * @param fundId - The ID of the fund to get reporting periods for
 * @returns Promise resolving to an array of ReportingPeriod objects
 */
export const getReportingPeriods = async (
  fundId: string
): Promise<ReportingPeriod[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/ReportingPeriods`,
      buildClientAndFundHeader(Number(fundId))
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve reporting periods", response.statusText);
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error("Error getting reporting periods:", error);
  }

  return [];
};

/**
 * Creates a new reporting period for a fund
 * @param fundId - The ID of the fund to create the reporting period for
 * @param period - The reporting period details to create
 * @returns Promise resolving to the created ReportingPeriod or null if creation failed
 */
export const createReportingPeriod = async (
  fundId: string | number,
  period: CreateReportingPeriodRequest
): Promise<ReportingPeriod | null> => {
  try {
    const response = await axios.post(
      `${getServiceEndpoint()}/ReportingPeriods`,
      JSON.stringify({
        ...period,
        fundId: fundId,
      }),
      buildClientHeader("json")
    );

    if (response.status !== 200) {
      console.error("Failed to create reporting period", response.statusText);
      return null;
    }

    if (response.data && typeof response.data === "object") {
      return response.data;
    }

    const periodsResponse = await getReportingPeriods(String(fundId));
    if (periodsResponse && periodsResponse.length > 0) {
      const newlyCreatedPeriod = periodsResponse.find(
        (p) => p.period === period.period && p.isOpen === period.isOpen
      );
      return newlyCreatedPeriod || periodsResponse[0];
    }

    return null;
  } catch (error) {
    console.error("Error creating reporting period:", error);
    return null;
  }
};

/**
 * Updates an existing reporting period
 * @param period - The reporting period object with updated values
 * @returns Promise resolving to an array of ReportingPeriod objects after update
 */
export const updateReportingPeriod = async (
  period: ReportingPeriod
): Promise<ReportingPeriod[]> => {
  try {
    const response = await axios.post(
      `${getServiceEndpoint()}/ReportingPeriods`,
      JSON.stringify({
        ...period,
        fundId: period.fundId,
        reportingPeriodId: period.reportingPeriodId,
      }),
      buildClientHeader("json")
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to update reporting period", response.statusText);
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error("Error updating reporting period:", error);
  }

  return [];
};
